import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { logout } from '../store/authSlice';
import Sidebar from '../components/common/Sidebar';
import SwotRingChart from '../components/common/SwotRingChart';
import httpClient from '../api/httpClient';
import { getOrgDepartmentLabels, onboardingApi } from '../api/onboardingApi';
import { vibeService } from '../services/VibeService';
import AnalyticsStyleEmptyState from '../components/common/AnalyticsStyleEmptyState';
import './Analytics.css';

// Types for API response
interface SubDimension {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  source?: string;
  points?: string[];
}

interface Dimension {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  sub_dimensions: SubDimension[];
}

interface Area {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  dimensions: Dimension[];
}

interface FocusRecommendation {
  skill: string;
  score: number;
  tip: string;
}

interface InsightsData {
  success: boolean;
  user_id: string;
  skill_scores: {
    communication: number;
    leadership: number;
    problem_solving: number;
    technical: number;
    creativity: number;
    adaptability: number;
  };
  overall_score: number;
  progress: { week: string; overall: number }[];
  session_counts: {
    Interview: number;
    GD: number;
    Coding: number;
  };
  sunburst_analysis: {
    areas: Area[];
  } | null;
  focus_recommendations: FocusRecommendation[];
  last_updated: string;
  error?: string | null;
}

// Get skill color based on score
const getSkillColor = (score: number): string => {
  if (score >= 85) return '#00FF88'; // Green - Strength
  if (score >= 70) return '#7FFF00'; // Lime - Opportunity
  if (score >= 50) return '#BA68C8'; // Purple - Weakness
  return '#008B8B'; // Teal - Critical
};

// Get category label
const getCategoryLabel = (score: number): string => {
  if (score >= 85) return 'Strength';
  if (score >= 70) return 'Improvements';
  if (score >= 50) return 'Weakness';
  return 'Critical';
};

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const rootRef = useRef<HTMLDivElement>(null);
  const [insightsData, setInsightsData] = useState<InsightsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string | undefined>();
  const [departmentName, setDepartmentName] = useState<string | undefined>();

  // Fetch user profile for first_name/last_name from student table
  useEffect(() => {
    if (!user?.id) return;
    const loadProfile = async () => {
      try {
        const profileResponse = await onboardingApi.getUserProfile();
        const { first_name, last_name } = profileResponse.data;
        if (first_name && last_name) {
          setDisplayName(`${first_name} ${last_name}`.trim());
        } else if (first_name || last_name) {
          setDisplayName((first_name || last_name || '').trim());
        } else {
          setDisplayName(user?.email?.split('@')[0] || 'User');
        }
        const orgDept = getOrgDepartmentLabels(profileResponse.data);
        setOrganizationName(orgDept.organizationName);
        setDepartmentName(orgDept.departmentName);
      } catch {
        setDisplayName(user?.email?.split('@')[0] || 'User');
        setOrganizationName(undefined);
        setDepartmentName(undefined);
      }
    };
    loadProfile();
  }, [user?.id, user?.email]);

  const fullName = displayName || user?.email?.split('@')[0] || 'User';
  const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const fetchInsights = useCallback(async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      vibeService.trigger('loading');
      setError(null);
      const response = await httpClient.get(`ai/insights/${user.id}`);
      
      if (response.data.success) {
        const rawData = response.data;
        const insightsPayload = rawData.insights || rawData;
        
        const normalizedData: InsightsData = {
          success: rawData.success,
          user_id: rawData.user_id,
          skill_scores: insightsPayload.skill_scores,
          overall_score: insightsPayload.overall_score,
          progress: insightsPayload.progress,
          session_counts: insightsPayload.session_counts,
          sunburst_analysis: insightsPayload.sunburst_analysis,
          focus_recommendations: insightsPayload.focus_recommendations,
          last_updated: insightsPayload.last_updated,
          error: insightsPayload.error,
        };
        
        setInsightsData(normalizedData);
        
        // Trigger vibe based on overall score
        const overallScore = normalizedData.overall_score;
        if (overallScore >= 80) {
          vibeService.trigger('celebration');
        } else if (overallScore >= 70) {
          vibeService.trigger('almost_there');
        } else if (overallScore >= 50) {
          vibeService.trigger('dont_give_up');
        } else if (overallScore >= 30) {
          vibeService.trigger('level_up');
        } else {
          vibeService.trigger('hustle');
        }
      } else {
        setError(response.data.error || 'Failed to load insights');
      }
    } catch (err: any) {
      console.error('Failed to fetch insights:', err);
      setError(err.response?.data?.error || 'Failed to load insights');
      vibeService.trigger('error');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleStartInterview = () => {
    navigate('/interview');
  };

  // Check if data is empty
  const hasData = insightsData && 
    insightsData.sunburst_analysis?.areas && 
    insightsData.sunburst_analysis.areas.length > 0 &&
    insightsData.sunburst_analysis.areas.some(area => 
      area.dimensions.some(dim => 
        dim.sub_dimensions.some(sub => sub.score !== null)
      )
    );

  // Format last updated date
  const lastUpdated = insightsData?.last_updated 
    ? new Date(insightsData.last_updated).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    : null;

  // Skill scores array for display
  const skillScores = insightsData?.skill_scores ? [
    { name: 'Communication', key: 'communication', score: insightsData.skill_scores.communication },
    { name: 'Leadership', key: 'leadership', score: insightsData.skill_scores.leadership },
    { name: 'Problem Solving', key: 'problem_solving', score: insightsData.skill_scores.problem_solving },
    { name: 'Technical', key: 'technical', score: insightsData.skill_scores.technical },
    { name: 'Creativity', key: 'creativity', score: insightsData.skill_scores.creativity },
    { name: 'Adaptability', key: 'adaptability', score: insightsData.skill_scores.adaptability },
  ] : [];

  // Loading State Component
  const LoadingState = () => (
    <div className="analytics-loading-state">
      <div className="loading-spinner"></div>
      <p>Loading your insights...</p>
    </div>
  );

  return (
    <div className="analytics-page" ref={rootRef}>
      {/* Fixed Sidebar */}
      <div style={{ position: 'fixed', left: 0, top: 0, height: '100vh', zIndex: 100 }}>
        <Sidebar
          activeNav="achievements"
          onSelectNav={(key) => {
            if (key === 'overview') navigate('/candidate');
            else if (key === 'skills') navigate('/candidate', { state: { activeNav: 'skills' } });
            else if (key === 'activity') navigate('/candidate', { state: { activeNav: 'activity' } });
            else if (key === 'interviews') navigate('/candidate', { state: { activeNav: 'interviews' } });
          }}
          initials={initials}
          name={fullName}
          email={user?.email}
          organizationName={organizationName}
          departmentName={departmentName}
          onStartInterview={handleStartInterview}
          onLogout={handleLogout}
        />
      </div>

      {/* Main Content */}
      <div className="analytics-content" style={{ marginLeft: '260px' }}>
        {/* Header Section - matching dashboard style */}
        <div className="welcome-section">
          <div className="welcome-left">
            <h1>Analytics Overview</h1>
            <p className="welcome-message">Your comprehensive skill assessment and SWOT analysis</p>
          </div>
          <div className="welcome-right">
            {lastUpdated && <span className="analytics-updated">Last updated: {lastUpdated}</span>}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && <LoadingState />}

        {/* Error State */}
        {!isLoading && error && (
          <div className="analytics-error-state">
            <p>{error}</p>
            <button onClick={fetchInsights}>Try Again</button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && !hasData && (
          <AnalyticsStyleEmptyState
            title="No Insights Yet"
            description="Complete interviews, group discussions, or coding challenges to generate your personalized SWOT analysis and skill insights."
            icon={
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
            onPrimary={handleStartInterview}
            onSecondary={() => navigate('/candidate')}
            hints={[
              { icon: '💬', text: 'Complete at least one session to see your analysis' },
              { icon: '📊', text: 'More sessions = more accurate insights' },
            ]}
          />
        )}

        {/* Data Content */}
        {!isLoading && !error && hasData && insightsData && (
          <>
            {/* Summary Stats - matching dashboard style */}
            <div className="analytics-summary-grid">
              <div className="analytics-summary-card overall">
                <div className="summary-card-info">
                  <div className="summary-card-label">Overall Score</div>
                  <div className="summary-card-category" style={{ color: getSkillColor(insightsData.overall_score || 0) }}>{getCategoryLabel(insightsData.overall_score || 0)}</div>
                </div>
                <div className="summary-card-value">
                  {insightsData.overall_score ?? '-'}
                </div>
              </div>
              <div className="analytics-summary-card">
                <div className="summary-card-label">Interview Sessions</div>
                <div className="summary-card-value">{insightsData.session_counts?.Interview ?? 0}</div>
              </div>
              <div className="analytics-summary-card">
                <div className="summary-card-label">Group Discussions</div>
                <div className="summary-card-value">{insightsData.session_counts?.GD ?? 0}</div>
              </div>
              <div className="analytics-summary-card">
                <div className="summary-card-label">Coding Sessions</div>
                <div className="summary-card-value">{insightsData.session_counts?.Coding ?? 0}</div>
              </div>
            </div>

            {/* Skill Scores Section */}
            <div className="analytics-section">
              <div className="section-header">
                <h3>Skill Scores</h3>
                <span className="section-subtitle">Based on all completed sessions</span>
              </div>
              <div className="skill-scores-grid">
                {skillScores.map(skill => (
                  <div key={skill.key} className="skill-score-card">
                    <div className="skill-score-header">
                      <span className="skill-score-name">{skill.name}</span>
                      <span className="skill-score-value" style={{ color: getSkillColor(skill.score) }}>
                        {skill.score}
                      </span>
                    </div>
                    <div className="skill-score-bar">
                      <div 
                        className="skill-score-bar-fill" 
                        style={{ 
                          width: `${skill.score}%`,
                          backgroundColor: getSkillColor(skill.score)
                        }} 
                      />
                    </div>
                    <div className="skill-score-category" style={{ color: getSkillColor(skill.score) }}>
                      {getCategoryLabel(skill.score)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SWOT Ring Chart Section */}
            <div className="analytics-section sunburst-section">
              <div className="section-header">
                <h3>SWOT Analysis - Skill Hierarchy</h3>
                <span className="section-subtitle">Click on segments to explore dimensions</span>
              </div>
              <div className="sunburst-wrapper">
                <SwotRingChart 
                  areas={insightsData.sunburst_analysis!.areas}
                  size={500}
                />
              </div>
              
            </div>

            {/* Focus Recommendations */}
            {insightsData.focus_recommendations && insightsData.focus_recommendations.length > 0 && (
              <div className="analytics-section">
                <div className="section-header">
                  <h3>Focus Recommendations</h3>
                  <span className="section-subtitle">Priority areas for improvement</span>
                </div>
                <div className="recommendations-list">
                  {insightsData.focus_recommendations.map((rec, index) => (
                    <div key={rec.skill} className="recommendation-card">
                      <div className="recommendation-header">
                        <div className="recommendation-rank">#{index + 1}</div>
                        <div className="recommendation-skill">
                          <span className="skill-name">{rec.skill.charAt(0).toUpperCase() + rec.skill.slice(1)}</span>
                          <span className="skill-score" style={{ color: getSkillColor(rec.score) }}>
                            Score: {rec.score}
                          </span>
                        </div>
                      </div>
                      <p className="recommendation-tip">{rec.tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;
